// The typed event catalog: the machine-readable source of truth for docs/ANALYTICS.md.
// Every event name and property shape the product may emit is defined here.
// Schema version bumps when an event's meaning or properties change incompatibly.

export const ANALYTICS_SCHEMA_VERSION = 1;

type Src = "local" | "model";

export interface EventCatalog {
  // Arrival and activation
  "landing.viewed": { referrerHost?: string };
  "landing.directive_submitted": { emotionTerms: string[]; length: number };
  "song.generated": { source: Src; ms: number; emotion: string };
  "song.first_audio": { msFromSubmit: number };
  "studio.entered": { via: "landing" | "universe" | "link" | "fork" };
  "draft.created": { via: "directive" | "blank" | "fork" | "starting_point" };
  "draft.resumed": Record<string, never>;
  "auth.signup": { via: "password" | "magic" | "google"; claimedObjects: number };
  "auth.signin": { via: "password" | "magic" | "google" };

  // Creation
  "block.added": { blockType: string; via: "manual" | "generation" | "pull" | "install" };
  "block.edited": { blockType: string; editor: "canvas" | "arrangement" | "sketch" | "mix" | "lyrics" };
  "sketch.previewed": { blockType: string };
  "arrangement.opened": Record<string, never>;
  "mix.opened": Record<string, never>;
  "lyrics.opened": Record<string, never>;
  "take.recorded": { seconds: number };
  "instrument.changed": { family: string };
  "patch.generated": { source: Src };
  "directive.issued": { surface: "commandbar" | "chip" | "flow" | "mix_intent"; length: number };
  "directive.applied": { source: Src; opsCount: number };
  "directive.dismissed": { source: Src };
  "suggestion.accepted": { facet: string };
  "suggestion.rejected": { facet: string };
  "note.acted": { trigger: string };
  "explain.opened": { control: string };
  "explain.heard": { control: string };

  // History and universe
  "timemachine.opened": Record<string, never>;
  "timemachine.scrubbed": Record<string, never>;
  "timemachine.forked": { fromAgeMs: number };
  "iteration.marked": Record<string, never>;
  "iteration.compared": Record<string, never>;
  "universe.opened": { lens: "graph" | "search" | "eras" };
  "memory.searched": { mode: "text" | "dna" | "emotion" };
  "memory.result_used": Record<string, never>;
  "idea.revived": { dormantDays: number };

  // Together
  "jam.invited": Record<string, never>;
  "jam.joined": { participants: number };
  "room.joined": { listeners: number };
  "session.published": { blocks: number; iterations: number };
  "session.played": Record<string, never>;
  "session.forked": Record<string, never>;
  "session.commented": { anchor: "block" | "time" | "none" };
  "creator.followed": Record<string, never>;
  "challenge.joined": Record<string, never>;
  "blocks.pulled": { fromSession: boolean; count: number };

  // Ecosystem
  "pack.viewed": { packType: string };
  "pack.previewed": { packType: string };
  "pack.installed": { packType: string; price: number };
  "pack.published": { packType: string };
  "revenue.purchase": { amount: number; packType: string };
  "extension.installed": Record<string, never>;
  "extension.ran": { capabilities: string[] };
  "lesson.started": { lesson: string };
  "lesson.completed": { lesson: string };
  "achievement.earned": { achievement: string };
  "wrapped.viewed": { year: number };
  "wrapped.shared": { year: number };

  // Outcomes and system
  "export.completed": { projection: "wav" | "stems" | "mp3" | "midi" | "package" };
  "share.link_created": Record<string, never>;
  "stage.launched": Record<string, never>;
  "performance.captured": { seconds: number };
  "sync.conflict": Record<string, never>;
  "error.surfaced": { code: string; surface: string };
  "ai.fallback": { route: string; reason: "no_key" | "timeout" | "error" | "invalid" };
  "consent.updated": { level: "all" | "none" };

  // Performance (docs/PERFORMANCE.md tooling)
  "perf.vitals": { lcp?: number; cls?: number; inp?: number; route: string };
  "perf.block": { ms: number; surface: string };
  "perf.audio": { jitterMs: number; inputToSoundMs: number };
  "perf.frame": { fps: number; longFrames: number; surface: string };
  "experiment.assigned": { name: string; variant: string };
}

export type EventName = keyof EventCatalog;
